import { useEffect, useCallback } from 'react';
import { fetchImageProxy } from '@/api/generation';
import { Capacitor } from '@capacitor/core';
import { APP_NAME } from '@/config/brand';
import { track } from '@/utils/tracker';
import { saveGenerationImageToAlbum } from '@/utils/saveGenerationImage';

interface ShareSheetProps {
  isOpen: boolean;
  imageUrl: string;
  prompt: string;
  onClose: () => void;
  genId?: string;
}

/**
 * Headless save/share component — no UI popup.
 * On iOS native: saves directly to Photos via PHPhotoLibrary (no share sheet).
 * On web: triggers navigator.share or URL-only share as fallback.
 */
export default function ShareSheet({ isOpen, imageUrl, prompt, onClose, genId }: ShareSheetProps) {
  const doShare = useCallback(async () => {
    const fileName = `nanoimage2_${Date.now()}.jpg`;

    // iOS native app: save directly to Photos without share sheet
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      try {
        if (genId) {
          await saveGenerationImageToAlbum({ genId, imageUrl, quality: 'sd' });
        } else {
          const blob = await fetchImageProxy(imageUrl);
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          await window.__NATIVE_BRIDGE__?.saveImage?.(base64);
        }
        track.shareSuccess('ios_save', genId);
      } catch {
        // fallback: open share sheet with URL
        if (navigator.share) {
          try {
            await navigator.share({ url: imageUrl, title: APP_NAME, text: prompt });
            track.shareSuccess('ios_url_share', genId);
          } catch { /* cancelled */ }
        }
      }
      onClose();
      return;
    }

    // Web: use file share if supported, else URL share
    try {
      const blob = genId
        ? await saveGenerationImageToAlbum({ genId, imageUrl, quality: 'sd' })
        : await fetchImageProxy(imageUrl);
      const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: APP_NAME, text: prompt });
          track.shareSuccess('web_file_share', genId);
        } catch {
          // user cancelled
        }
        onClose();
        return;
      }
    } catch {
      // proxy failed — fall through
    }
    if (navigator.share) {
      try {
        await navigator.share({ url: imageUrl, title: APP_NAME, text: prompt });
        track.shareSuccess('web_url_share', genId);
      } catch {
        // cancelled or not supported
      }
    }
    onClose();
  }, [imageUrl, prompt, onClose, genId]);

  useEffect(() => {
    if (!isOpen) return;
    doShare();
  }, [isOpen, doShare]);

  return null;
}
